/**
 * DD Assistant Factory
 *
 * Creates the appropriate DD Assistant implementation based on configuration,
 * feature flags, and environment variables.
 *
 * In MOCK MODE: Returns MockAssistant with simulated responses (no network calls)
 * In DEMO/PRODUCTION MODE: Returns real assistant (PromptBased or RAG)
 */

import { PromptBasedAssistant } from './prompt-based-assistant';
import { RAGAssistant } from './rag-assistant';
import { MockAssistant } from './mock-assistant';
import {
  type IDDAssistant,
  type DDAssistantMode,
  type DDAssistantConfig,
  type DDFeatureFlags,
  DEFAULT_DD_FEATURE_FLAGS,
  DEFAULT_RAG_CONFIG,
  resolveAssistantMode,
} from './assistant.types';
import { mode, APP_MODE } from '@/lib/config/app-mode';

// ============================================
// Environment Configuration
// ============================================

/**
 * Get DD mode from environment variable.
 * Set EXPO_PUBLIC_DD_MODE=rag to enable RAG mode globally.
 * Returns null in mock mode (mock assistant used regardless of this setting).
 */
function getEnvMode(): DDAssistantMode | null {
  // In mock mode, always use mock assistant
  if (mode.isMock) {
    return null;
  }

  const envMode = process.env.EXPO_PUBLIC_DD_MODE;
  if (envMode === 'rag' || envMode === 'mvp') {
    return envMode;
  }
  return null;
}

/**
 * Get feature flags from environment or remote config.
 * In production, these would come from a feature flag service.
 */
function getFeatureFlags(): DDFeatureFlags {
  // Check for environment overrides
  const envMode = getEnvMode();
  if (envMode) {
    return {
      ...DEFAULT_DD_FEATURE_FLAGS,
      ddAssistantMode: envMode,
    };
  }

  // In a real implementation, this would fetch from:
  // - Supabase feature_flags table
  // - LaunchDarkly
  // - PostHog
  // - etc.

  // For now, return defaults (MVP mode)
  return DEFAULT_DD_FEATURE_FLAGS;
}

// ============================================
// Factory Functions
// ============================================

/**
 * Create a DD Assistant instance.
 *
 * @param config - Optional configuration override
 * @returns IDDAssistant implementation
 *
 * @example
 * // Use default mode (from env/feature flags)
 * const assistant = createDDAssistant();
 *
 * @example
 * // Force specific mode
 * const assistant = createDDAssistant({ mode: 'rag' });
 *
 * @example
 * // Use feature flags with user context
 * const mode = resolveAssistantMode(flags, userId, userTier);
 * const assistant = createDDAssistant({ mode });
 */
export function createDDAssistant(config?: Partial<DDAssistantConfig>): IDDAssistant {
  // In MOCK MODE, always return MockAssistant (no network calls)
  if (mode.isMock) {
    if (__DEV__) console.log('[DD Assistant] Using MockAssistant (mock mode)');
    return new MockAssistant();
  }

  // Determine mode for demo/production
  const flags = getFeatureFlags();
  const assistantMode = config?.forceMode ?? config?.mode ?? flags.ddAssistantMode;

  if (__DEV__) console.log(`[DD Assistant] Using ${assistantMode.toUpperCase()} mode (${APP_MODE} environment)`);

  // Create appropriate implementation
  switch (assistantMode) {
    case 'rag':
      return new RAGAssistant(config?.ragConfig);

    case 'mvp':
    default:
      return new PromptBasedAssistant();
  }
}

/**
 * Create a DD Assistant for a specific user, using feature flags for mode resolution.
 *
 * @param userId - User ID for A/B test bucketing
 * @param userTier - User subscription tier for tier-gating
 * @param config - Optional configuration override
 * @returns IDDAssistant implementation
 *
 * @example
 * const assistant = createDDAssistantForUser(user.id, user.tier);
 */
export function createDDAssistantForUser(
  userId: string,
  userTier: string,
  config?: Partial<DDAssistantConfig>
): IDDAssistant {
  const flags = getFeatureFlags();
  const resolvedMode = resolveAssistantMode(flags, userId, userTier);

  return createDDAssistant({
    ...config,
    mode: config?.forceMode ?? resolvedMode,
  });
}

// ============================================
// React Hook Helper
// ============================================

/**
 * Hook-friendly factory that returns a stable assistant reference.
 * Use this in React components with useMemo.
 *
 * @example
 * const assistant = useMemo(
 *   () => getDDAssistant(userId, userTier),
 *   [userId, userTier]
 * );
 */
export function getDDAssistant(userId?: string, userTier?: string): IDDAssistant {
  if (userId && userTier) {
    return createDDAssistantForUser(userId, userTier);
  }
  return createDDAssistant();
}

// ============================================
// Type Exports
// ============================================

export type { DDAssistantConfig, DDFeatureFlags };
export { DEFAULT_RAG_CONFIG, DEFAULT_DD_FEATURE_FLAGS };
